'use client';

import {
  forwardRef,
  KeyboardEvent,
  MouseEventHandler,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { WandSparkles } from 'lucide-react';

import { useTranslations } from '@northladder/i18n';

import { cn } from '../../lib';

import { Popover } from '../popover';

import { SelectPopOverContent } from './SelectPopOverContent';
import { TriggerBtn } from './TriggerBtn';
import type {
  IMultiSelectGroup,
  IMultiSelectOption,
  IMultiSelectProps,
  IMultiSelectRef,
} from './types';

/**
 * -----------------------------------------------------------------------------
 * This renders a dropdown component with options to allow the user to select
 * a single or multiple options.
 * todo: @divyansh Also add links to the relevant documentation.
 * TODO: Remove the forward ref later since its not relevant in react 19.
 */
export const MultiSelect = forwardRef<IMultiSelectRef, IMultiSelectProps>(
  (
    {
      options,
      onValueChange,
      variant,
      defaultValue = [],
      placeholder: placeholderInit,
      animation = 0,
      animationConfig: animationConfigInit,
      maxCount = 1,
      modalPopover = false,
      asChild = false,
      className,
      hideSelectAll = false,
      searchable = true,
      emptyIndicator,
      autoSize = false,
      singleLine = false,
      popoverClassName,
      disabled = false,
      responsive,
      minWidth,
      maxWidth,
      placeholderClassName,
      deduplicateOptions = false,
      resetOnDefaultValueChange = true,
      closeOnSelect = false,
      ...props
    },
    ref
  ) => {
    const t = useTranslations('DesignSystemComponents.MultiSelect');

    const placeholder = placeholderInit || t('placeholder');

    const animationConfig = animationConfigInit || {
      badgeAnimation: 'bounce',
      popoverAnimation: 'scale',
    };

    const [selectedValues, setSelectedValues] =
      useState<string[]>(defaultValue);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [searchValue, setSearchValue] = useState('');

    const [politeMessage, setPoliteMessage] = useState('');
    const [assertiveMessage, setAssertiveMessage] = useState('');
    const prevSelectedCount = useRef(selectedValues.length);
    const prevIsOpen = useRef(isPopoverOpen);
    const prevSearchValue = useRef(searchValue);

    const announce = useCallback(
      (message: string, priority: 'polite' | 'assertive' = 'polite') => {
        if (priority === 'assertive') {
          setAssertiveMessage(message);
          setTimeout(() => setAssertiveMessage(''), 100);
        } else {
          setPoliteMessage(message);
          setTimeout(() => setPoliteMessage(''), 100);
        }
      },
      []
    );

    const handleOnSearchValueChange = (value: string) => {
      setSearchValue(value);
    };

    const multiSelectId = useId();
    const listboxId = `${multiSelectId}-listbox`;
    const triggerDescriptionId = `${multiSelectId}-description`;
    const selectedCountId = `${multiSelectId}-count`;

    const prevDefaultValueRef = useRef<string[]>(defaultValue);

    const isGroupedOptions = useCallback(
      (
        opts: IMultiSelectOption[] | IMultiSelectGroup[]
      ): opts is IMultiSelectGroup[] =>
        opts.length > 0 && opts[0] !== undefined && 'heading' in opts[0],
      []
    );

    const arraysEqual = useCallback((a: string[], b: string[]): boolean => {
      if (a.length !== b.length) return false;
      const sortedA = [...a].sort();
      const sortedB = [...b].sort();
      return sortedA.every((val, index) => val === sortedB[index]);
    }, []);

    const resetToDefault = useCallback(() => {
      setSelectedValues(defaultValue);
      setIsPopoverOpen(false);
      setSearchValue('');
      onValueChange(defaultValue);
    }, [defaultValue, onValueChange]);

    const buttonRef = useRef<HTMLButtonElement>(null);

    useImperativeHandle(
      ref,
      () => ({
        reset: resetToDefault,
        getSelectedValues: () => selectedValues,
        setSelectedValues: (values: string[]) => {
          setSelectedValues(values);
          onValueChange(values);
        },
        clear: () => {
          setSelectedValues([]);
          onValueChange([]);
        },
        focus: () => {
          if (buttonRef.current) {
            buttonRef.current.focus();
            const originalOutline = buttonRef.current.style.outline;
            const originalOutlineOffset = buttonRef.current.style.outlineOffset;
            buttonRef.current.style.outline = '2px solid hsl(var(--ring))';
            buttonRef.current.style.outlineOffset = '2px';

            setTimeout(() => {
              if (buttonRef.current) {
                buttonRef.current.style.outline = originalOutline;
                buttonRef.current.style.outlineOffset = originalOutlineOffset;
              }
            }, 1000);
          }
        },
      }),
      [resetToDefault, selectedValues, onValueChange]
    );

    const [screenSize, setScreenSize] = useState<
      'mobile' | 'tablet' | 'desktop'
    >('desktop');

    useEffect(() => {
      if (typeof window === 'undefined') return undefined;

      const handleResize = () => {
        const width = window.innerWidth;
        if (width < 640) {
          setScreenSize('mobile');
        } else if (width < 1024) {
          setScreenSize('tablet');
        } else {
          setScreenSize('desktop');
        }
      };
      handleResize();

      window.addEventListener('resize', handleResize);

      return () => {
        if (typeof window !== 'undefined') {
          window.removeEventListener('resize', handleResize);
        }
      };
    }, []);

    const getResponsiveSettings = () => {
      if (!responsive) {
        return {
          maxCount,
          hideIcons: false,
          compactMode: false,
        };
      }

      if (responsive === true) {
        const defaultResponsive = {
          mobile: { maxCount: 2, hideIcons: false, compactMode: true },
          tablet: { maxCount: 4, hideIcons: false, compactMode: false },
          desktop: { maxCount: 6, hideIcons: false, compactMode: false },
        };
        const currentSettings = defaultResponsive[screenSize];

        return {
          maxCount: currentSettings?.maxCount ?? maxCount,
          hideIcons: currentSettings?.hideIcons ?? false,
          compactMode: currentSettings?.compactMode ?? false,
        };
      }
      const currentSettings = responsive[screenSize];

      return {
        maxCount: currentSettings?.maxCount ?? maxCount,
        hideIcons: currentSettings?.hideIcons ?? false,
        compactMode: currentSettings?.compactMode ?? false,
      };
    };

    const responsiveSettings = getResponsiveSettings();

    const getBadgeAnimationClass = () => {
      if (animationConfig?.badgeAnimation) {
        switch (animationConfig.badgeAnimation) {
          case 'bounce':
            return isAnimating
              ? 'animate-bounce'
              : 'hover:-translate-y-1 hover:scale-110';
          case 'pulse':
            return 'hover:animate-pulse';
          case 'wiggle':
            return 'hover:animate-wiggle';
          case 'fade':
            return 'hover:opacity-80';
          case 'slide':
            return 'hover:translate-x-1';
          case 'none':
            return '';
          default:
            return '';
        }
      }

      return isAnimating ? 'animate-bounce' : '';
    };

    const getPopoverAnimationClass = () => {
      if (animationConfig?.popoverAnimation) {
        switch (animationConfig.popoverAnimation) {
          case 'scale':
            return 'animate-scaleIn';
          case 'slide':
            return 'animate-slideInDown';
          case 'fade':
            return 'animate-fadeIn';
          case 'flip':
            return 'animate-flipIn';
          case 'none':
            return '';
          default:
            return '';
        }
      }
      return '';
    };

    const getAllOptions = useCallback((): IMultiSelectOption[] => {
      if (options.length === 0) return [];

      let allOptions: IMultiSelectOption[];

      if (isGroupedOptions(options)) {
        allOptions = options.flatMap((group) => group.options);
      } else {
        allOptions = options;
      }

      const valueSet = new Set<string>();
      const duplicates: string[] = [];
      const uniqueOptions: IMultiSelectOption[] = [];

      allOptions.forEach((option) => {
        if (valueSet.has(option.value)) {
          duplicates.push(option.value);

          if (!deduplicateOptions) {
            uniqueOptions.push(option);
          }
        } else {
          valueSet.add(option.value);
          uniqueOptions.push(option);
        }
      });

      if (process.env.NODE_ENV === 'development' && duplicates.length > 0) {
        const action = deduplicateOptions
          ? 'automatically removed'
          : 'detected';

        // eslint-disable-next-line no-console
        console.warn(
          `MultiSelect: Duplicate option values ${action}: ${duplicates.join(
            ', '
          )}. ` +
            `${
              deduplicateOptions
                ? 'Duplicates have been removed automatically.'
                : "This may cause unexpected behavior. Consider setting 'deduplicateOptions={true}' or ensure all option values are unique."
            }`
        );
      }
      return deduplicateOptions ? uniqueOptions : allOptions;
    }, [options, deduplicateOptions, isGroupedOptions]);

    const getOptionByValue = useCallback(
      (value: string): IMultiSelectOption | undefined => {
        const option = getAllOptions().find((item) => item.value === value);

        if (!option && process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.warn(
            `MultiSelect: Option with value "${value}" not found in options list`
          );
        }

        return option;
      },
      [getAllOptions]
    );

    const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        setIsPopoverOpen(true);
      } else if (event.key === 'Backspace' && !event.currentTarget.value) {
        const newSelectedValues = [...selectedValues];
        newSelectedValues.pop();

        setSelectedValues(newSelectedValues);
        onValueChange(newSelectedValues);
      }
    };

    const handleOnToggleOption = (optionValue: string) => {
      if (disabled) return;

      const option = getOptionByValue(optionValue);
      if (option?.disabled) return;

      const newSelectedValues = selectedValues.includes(optionValue)
        ? selectedValues.filter((value) => value !== optionValue)
        : [...selectedValues, optionValue];
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);

      if (closeOnSelect) {
        setIsPopoverOpen(false);
      }
    };

    const handleTogglePopover = () => {
      if (disabled) return;

      setIsPopoverOpen((prev) => !prev);
    };

    const handleOnClosePopover = () => {
      setIsPopoverOpen(false);
    };

    const handleOnClearExtraOptions: MouseEventHandler<SVGSVGElement> = (
      event
    ) => {
      event.stopPropagation();

      if (disabled) return;

      const newSelectedValues = selectedValues.slice(
        0,
        responsiveSettings.maxCount
      );

      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    };

    const handleOnClearFilters = () => {
      if (disabled) return;

      setSelectedValues([]);
      onValueChange([]);
    };

    const handleOnPressSparkles = () => {
      setIsAnimating(!isAnimating);
    };

    const handleOnToggleAll = () => {
      if (disabled) return;

      const allOptions = getAllOptions().filter((option) => !option.disabled);

      if (selectedValues.length === allOptions.length) {
        handleOnClearFilters();
      } else {
        const allValues = allOptions.map((option) => option.value);
        setSelectedValues(allValues);
        onValueChange(allValues);
      }

      if (closeOnSelect) {
        setIsPopoverOpen(false);
      }
    };

    useEffect(() => {
      if (!resetOnDefaultValueChange) return;

      const prevDefaultValue = prevDefaultValueRef.current;

      if (!arraysEqual(prevDefaultValue, defaultValue)) {
        if (!arraysEqual(selectedValues, defaultValue)) {
          setSelectedValues(defaultValue);
        }
        prevDefaultValueRef.current = [...defaultValue];
      }
    }, [defaultValue, selectedValues, arraysEqual, resetOnDefaultValueChange]);

    const getWidthConstraints = () => {
      const defaultMinWidth = screenSize === 'mobile' ? '0px' : '200px';
      const effectiveMinWidth = minWidth || defaultMinWidth;
      const effectiveMaxWidth = maxWidth || '100%';

      return {
        minWidth: effectiveMinWidth,
        maxWidth: effectiveMaxWidth,
        width: autoSize ? 'auto' : '100%',
      };
    };

    const widthConstraints = getWidthConstraints();

    useEffect(() => {
      if (!isPopoverOpen) {
        setSearchValue('');
      }
    }, [isPopoverOpen]);

    useEffect(() => {
      const selectedCount = selectedValues.length;
      const allOptions = getAllOptions();
      const totalOptions = allOptions.filter((opt) => !opt.disabled).length;

      if (selectedCount !== prevSelectedCount.current) {
        const diff = selectedCount - prevSelectedCount.current;

        if (diff > 0) {
          const addedItems = selectedValues.slice(-diff);
          const addedLabels = addedItems
            .map(
              (value) => allOptions.find((opt) => opt.value === value)?.labelStr
            )
            .filter(Boolean);

          if (addedLabels.length === 1) {
            announce(
              t('announce.oneOptionSelected', {
                optionName: addedLabels[0] || t('announce.option'),
                selectedCount,
                totalOptions,
              })
            );
          } else {
            announce(
              t('announce.multipleOptionSelected', {
                count: addedLabels.length,
                selectedCount,
                totalOptions,
              })
            );
          }
        } else if (diff < 0) {
          announce(
            t('announce.optionRemoved', { selectedCount, totalOptions })
          );
        }
        prevSelectedCount.current = selectedCount;
      }

      if (isPopoverOpen !== prevIsOpen.current) {
        if (isPopoverOpen) {
          announce(t('announce.dropdownOpened', { totalOptions }));
        } else {
          announce(t('announce.dropdownClosed'));
        }
        prevIsOpen.current = isPopoverOpen;
      }

      if (
        searchValue !== prevSearchValue.current &&
        searchValue !== undefined
      ) {
        if (searchValue && isPopoverOpen) {
          const filteredCount = allOptions.filter(
            (opt) =>
              opt.labelStr.toLowerCase().includes(searchValue.toLowerCase()) ||
              opt.value.toLowerCase().includes(searchValue.toLowerCase())
          ).length;

          announce(
            t('announce.resultsSummary', {
              count: filteredCount,
              item: searchValue,
            })
          );
        }
        prevSearchValue.current = searchValue;
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedValues, isPopoverOpen, searchValue, announce, getAllOptions]);

    const animDelay = animationConfig?.delay || 0;
    const animDuration = animationConfig?.duration || animation;

    return (
      <>
        <div className="sr-only">
          <div aria-atomic="true" aria-live="polite" role="status">
            {politeMessage}
          </div>
          <div aria-atomic="true" aria-live="assertive" role="alert">
            {assertiveMessage}
          </div>
        </div>

        <Popover
          modal={modalPopover}
          onOpenChange={setIsPopoverOpen}
          open={isPopoverOpen}
        >
          <div className="sr-only" id={triggerDescriptionId}>
            {t('aria.screenReaderIntro')}
          </div>

          <div aria-live="polite" className="sr-only" id={selectedCountId}>
            {t('aria.screenReaderIntroSummary', {
              count: selectedValues.length,
              options: selectedValues
                .map((value) => getOptionByValue(value)?.labelStr)
                .filter(Boolean)
                .join(', '),
            })}
          </div>

          <TriggerBtn
            {...props}
            animDelay={animDelay}
            animDuration={animDuration}
            autoSize={autoSize}
            buttonRef={buttonRef}
            classBadgeAnimation={getBadgeAnimationClass()}
            isDisabled={disabled}
            isMobileScreen={screenSize === 'mobile'}
            isPopoverOpen={isPopoverOpen}
            isSingleLine={singleLine}
            listboxId={listboxId}
            onClearExtraOptions={handleOnClearExtraOptions}
            onClearFilters={handleOnClearFilters}
            onGetOptionByValue={getOptionByValue}
            onToggleOption={handleOnToggleOption}
            onTogglePopover={handleTogglePopover}
            placeholder={placeholder}
            placeholderClassName={placeholderClassName}
            responsiveSettings={responsiveSettings}
            selectedCountId={selectedCountId}
            selectedItemsCount={selectedValues.length}
            selectedValues={selectedValues}
            total={getAllOptions().length}
            triggerDescriptionId={triggerDescriptionId}
            variant={variant}
            widthConstraints={widthConstraints}
          />

          <SelectPopOverContent
            animDelay={animDelay}
            animDuration={animDuration}
            checkIfIsGroupedOptions={isGroupedOptions}
            classPopoverAnimation={getPopoverAnimationClass()}
            emptyIndicator={emptyIndicator}
            getAllOptions={getAllOptions}
            isSelectEnabled={!hideSelectAll}
            listboxId={listboxId}
            maxWidth={widthConstraints.maxWidth}
            multiSelectId={multiSelectId}
            onClearFilters={handleOnClearFilters}
            onClosePopover={handleOnClosePopover}
            onInputKeyDown={handleInputKeyDown}
            onSearchValueChange={handleOnSearchValueChange}
            onToggleAll={handleOnToggleAll}
            onToggleOption={handleOnToggleOption}
            options={options}
            popoverClassName={popoverClassName}
            screenSize={screenSize}
            searchable={searchable}
            searchValue={searchValue}
            selectedValues={selectedValues}
          />

          {animation > 0 && selectedValues.length > 0 ? (
            <WandSparkles
              className={cn(
                'text-foreground bg-background my-2 h-3 w-3 cursor-pointer',
                isAnimating ? '' : 'text-muted-foreground'
              )}
              onClick={handleOnPressSparkles}
            />
          ) : null}
        </Popover>
      </>
    );
  }
);

MultiSelect.displayName = 'MultiSelect';

export type { IMultiSelectGroup, IMultiSelectProps };
